/**
 * Storage Formatting Utilities
 * ============================
 * Helper functions for displaying storage capacities.
 */

/**
 * Standard marketed drive capacities in GB (decimal/manufacturer spec).
 * Drives report binary GB which is ~7% less than the marketed decimal value.
 * For example, a "10 TB" drive reports as ~9314 binary GB.
 */
const MARKETED_SIZES_GB = [
  120, 128, 240, 250, 256, 480, 500, 512, 750, 960,
  1000, 1024, 2000, 3000, 4000, 6000, 8000,
  10000, 12000, 14000, 16000, 18000, 20000, 22000, 24000,
]

/**
 * Convert raw binary GB (as reported by OS) to the nearest marketed drive capacity.
 * Returns a human-readable string like "10 TB" or "500 GB".
 *
 * The OS reports drive sizes in binary GB (1 GiB = 1,073,741,824 bytes),
 * but manufacturers label drives in decimal GB (1 GB = 1,000,000,000 bytes).
 * A "10 TB" drive actually has 10,000,000,000,000 bytes = ~9313 GiB.
 *
 * This function finds the nearest standard marketed size and formats it.
 */
export function formatDriveCapacity(binaryGb: number | null | undefined): string {
  if (!binaryGb) return '-'

  // Convert binary GB to approximate decimal GB (multiply by 1.073741824)
  const decimalGb = binaryGb * 1.073741824

  // Find the nearest marketed size
  let closest = MARKETED_SIZES_GB[0]
  let minDiff = Math.abs(decimalGb - closest)

  for (const size of MARKETED_SIZES_GB) {
    const diff = Math.abs(decimalGb - size)
    if (diff < minDiff) {
      minDiff = diff
      closest = size
    }
  }

  // Only use the marketed size if reasonably close (within 10%)
  const tolerance = closest * 0.10
  const marketedGb = minDiff <= tolerance ? closest : Math.round(decimalGb)

  // Format as TB or GB
  if (marketedGb >= 1000) {
    const tb = marketedGb / 1000
    return `${Number.isInteger(tb) ? tb : tb.toFixed(1)} TB`
  }
  return `${marketedGb} GB`
}

/**
 * Format a storage capacity value (already in usable/logical GB) for display.
 * Converts to TB when >= 1000 GB. Does NOT apply marketed-size rounding
 * since this is for volume/usable capacity, not raw drive sizes.
 */
export function formatStorageCapacity(gb: number | null | undefined): string | null {
  if (!gb) return null
  if (gb >= 1000) {
    return `${(gb / 1000).toFixed(1)} TB`
  }
  return `${gb} GB`
}
